import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatDate, INDUSTRY_VOICES_THEME } from '../../config';

function toMs(value) {
  if (!value) return 0;
  if (typeof value === 'string') return new Date(value).getTime() || 0;
  if (value?.toMillis) return value.toMillis();
  try {
    return new Date(value).getTime() || 0;
  } catch {
    return 0;
  }
}

function normaliseToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isIndustryVoices(post) {
  const want = normaliseToken(INDUSTRY_VOICES_THEME);
  const category = normaliseToken(post?.category);
  if (category === want) return true;
  const themes = Array.isArray(post?.themes) ? post.themes : [];
  return themes.some((t) => normaliseToken(t) === want);
}

function formatRelativeTime(ms) {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 0) return '';
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (min < 1) return 'just now';
  if (hr < 1) return `${min} min ago`;
  if (day < 1) return `${hr} hours ago`;
  if (day === 1) return '1 day ago';
  if (day < 14) return `${day} days ago`;
  // fallback to date for older content
  return formatDate(new Date(ms));
}

function SidebarList({ title, items }) {
  return (
    <div className="sidebar-block sidebar-feed-block">
      <div className="sidebar-feed-header">
        <div className="sidebar-feed-title">{title}</div>
        <span className="sidebar-feed-dot" aria-hidden="true" />
      </div>

      <div className="sidebar-feed-list" role="list">
        {items.map((it) => (
          <Link key={it.key} to={it.href} className="sidebar-feed-item" role="listitem">
            <div className="sidebar-feed-kicker">
              <span className="sidebar-feed-category">{it.categoryLabel}</span>
              {it.industryVoices && (
                <>
                  <span className="dot" aria-hidden="true" />
                  <span className="sidebar-feed-theme">{INDUSTRY_VOICES_THEME}</span>
                </>
              )}
            </div>
            <div className="sidebar-feed-headline">{it.title}</div>
            <div className="sidebar-feed-meta">
              {it.timeLabel ? <span className="secondary-text">{it.timeLabel}</span> : null}
              {it.typeLabel ? (
                <>
                  <span className="dot" aria-hidden="true" />
                  <span className="secondary-text">{it.typeLabel}</span>
                </>
              ) : null}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Sidebar() {
  const [posts, setPosts] = useState([]);
  const [slotReviews, setSlotReviews] = useState([]);

  useEffect(() => {
    const postsQ = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'), limit(250));
    const slotsQ = query(collection(db, 'slotReviews'), orderBy('createdAt', 'desc'), limit(80));

    const unsubPosts = onSnapshot(
      postsQ,
      (snap) => setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setPosts([])
    );

    const unsubSlots = onSnapshot(
      slotsQ,
      (snap) => setSlotReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setSlotReviews([])
    );

    return () => {
      unsubPosts();
      unsubSlots();
    };
  }, []);

  const unified = useMemo(() => {
    const postItems = posts
      .filter((p) => p && p.published !== false)
      .filter((p) => p && (p.slug || p.id))
      .map((p) => {
        const ms = toMs(p.publishedAt);
        const views = typeof p.views === 'number' ? p.views : 0;
        return {
          key: `post:${p.id || p.slug}`,
          href: `/news/${p.slug || p.id}`,
          title: String(p.title || 'Untitled post'),
          categoryLabel: String(p.category || 'News'),
          industryVoices: isIndustryVoices(p),
          typeLabel: 'News',
          timeMs: ms,
          timeLabel: formatRelativeTime(ms),
          views,
        };
      });

    const slotItems = slotReviews
      .filter((s) => s && (s.slug || s.id))
      .map((s) => {
        const ms = toMs(s.createdAt || s.updatedAt);
        const views = typeof s.views === 'number' ? s.views : 0;
        return {
          key: `slot:${s.id || s.slug}`,
          href: `/slots/${s.slug || s.id}`,
          title: String(s.title || 'Slot review'),
          categoryLabel: 'Slots review',
          industryVoices: false,
          typeLabel: 'Slots',
          timeMs: ms,
          timeLabel: formatRelativeTime(ms),
          views,
        };
      });

    return [...postItems, ...slotItems];
  }, [posts, slotReviews]);

  const latestFive = useMemo(() => {
    return [...unified]
      .sort((a, b) => (b.timeMs || 0) - (a.timeMs || 0))
      .slice(0, 5);
  }, [unified]);

  const hotFive = useMemo(() => {
    return [...unified]
      .sort((a, b) => {
        const dv = (b.views || 0) - (a.views || 0);
        if (dv !== 0) return dv;
        return (b.timeMs || 0) - (a.timeMs || 0);
      })
      .slice(0, 5);
  }, [unified]);

  return (
    <aside className="sidebar-column" aria-label="Latest and hot topics">
      <SidebarList title="Latest" items={latestFive} />
      <SidebarList title="Hot topics" items={hotFive} />
    </aside>
  );
}

export default Sidebar;


